import { DateTime } from 'luxon';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, SirenActionMixin, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-placeholder';
import '../../common/bn-icon';
import './scheduler-location';

class SchedulerLocations extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.locations = [];
  }

  static get properties() {
    return {
      locations: { type: Array },
      draggingHref: { type: String, attribute: false },
      selectedLocationHref: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      [draggable=true] {
        cursor: grab;
      }
      [draggable=true]:hover {
      }
      .drag-in-progress {
        opacity: 0.1;
      }
      .locations {
        display: flex;
        flex-direction: column;
      }
      .summary {
        display: flex;
        align-items: center;
        font-size: 1.2em;
      }
      .summary .date {
        font-weight: bold;
      }
      .summary .count {
        font-size: 1em;
        display: flex;
        align-items: center;
        margin-left: 1em;
      }
      .summary .count bn-icon {
        color: var(--primary);
        margin: 0 .5em 0 0;
      }
      .outstanding {
        background-color: var(--gray-30);
        border: 1px solid var(--gray-50);
        border-radius: 3px;
        padding-top: .5em;
        margin-bottom: 1em;
      }
      .no-data {
        text-align: center;
        padding: 1em;
      }
      .heading {
        margin-left: .5rem;
        margin-right: .5rem;
      }
      .map-marker-icon {
        margin: 0 .5em 0 0;
        height: 1.25em;
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`<bn-placeholder></bn-placeholder>`;
    }

    if (!this.locations.length) {
      return html`<div class="no-data">No work order locations for the selected date.</div>`;
    }

    const locationsByDate = this.locations.reduce((map, locationEntity) => {
      const { scheduledFor } = locationEntity.properties;
      if (map.has(scheduledFor)) {
        map.get(scheduledFor).push(locationEntity);
      } else {
        map.set(scheduledFor, [locationEntity]);
      }
      return map;
    }, new Map());

    const days = [];
    for (const [scheduledFor, locations] of locationsByDate) {
      days.push(this._renderDay(scheduledFor, locations));
    }

    return html`${days}`;
  }

  _renderDay(scheduledFor, locations) {
    const binCount = locations.reduce((count, location) => count + location.properties.workOrderCount, 0);
    const isOld = scheduledFor < DateTime.local().toFormat('yyyy-MM-dd');
    return html`
      <div class="day ${isOld ? 'outstanding' : ''}">
        <div class="heading">
          <div class="summary">
            <span class="date">
              ${DateTime.fromISO(scheduledFor).toLocaleString(DateTime.DATE_HUGE)}
            </span>
            <span class="count">
              <bn-icon name="fad fa-farm"></bn-icon>
              ${locations.length}
            </span>
            <span class="count">
              <img class="map-marker-icon" src="/images/map_marker_binsentry_blue.png"/>
              ${binCount}
            </span>
          </div>

          ${isOld ? 'Outstanding' : ''}
        </div>

        <div class="locations">
          ${renderIf(!locations.length, () => html`<p class="no-data">No Locations To Display</p>`)}
          ${locations.map((location) => this._renderLocation(location))}
        </div>
      </div>
    `;
  }

  _renderLocation(location) {
    const { href } = location.getLink('self');
    const isSelected = href === this.selectedLocationHref;
    return html`
      <scheduler-location
        class="${href === this.draggingHref ? 'drag-in-progress' : ''}"
        .href="${href}"
        draggable="true"
        @dragstart="${this._onDragStart}"
        @drag="${this._onDrag}"
        @dragend="${this._onDragEnd}"
        @response="${this._reload}"
        @click="${() => this._select(isSelected ? undefined : location)}"
        ?selected="${isSelected}"
      ></scheduler-location>
    `;
  }

  _select(location) {
    this.dispatchEvent(new CustomEvent('select', { detail: { location }, bubbles: true, composed: true }));
    this.selectedLocationHref = location && this.getHref('self', location);
  }

  _reload(e) {
    this.dispatchEvent(new CustomEvent('response', { detail: e.detail, bubbles: true, composed: true }));
  }

  updated(changedProperties) {
    if (changedProperties.has('locations')) {
      this.selectedLocationHref = undefined;
    }
    super.updated(changedProperties);
  }

  _onDragStart(e) {
    this.draggingHref = e.target.href;
    e.dataTransfer.dropEffect = 'move';
  }

  _onDrag(e) {
    let swap = this.shadowRoot.elementFromPoint(e.clientX, e.clientY);
    if (swap.parentNode !== e.target.parentNode || swap.href === this.draggingHref) {
      return;
    }
    let a = this.locations.findIndex(l => l.getLink('self').href === this.draggingHref); // index target entity
    let b = this.locations.findIndex(l => l.getLink('self').href === swap.href); // index of destination
    const [item] = this.locations.splice(a, 1);
    this.locations.splice(b, 0, item);
    this.update();
  }

  async _onDragEnd() {
    this.draggingHref = undefined;
    const hrefs = Array.from(this.shadowRoot.querySelectorAll('scheduler-location')).map(n => n.href);
    const updates = [];
    for (const location of this.locations) {
      const action = location.getAction('update-order');
      const newOrder = hrefs.indexOf(location.getLink('self').href) + 1;
      const orderField = action.getField('order');
      if (orderField.value === newOrder) {
        continue;
      }
      action.getField('order').value = newOrder;
      updates.push(this.submitAction(action));
    }
    await Promise.all(updates);
    if (updates.length) {
      this.dispatchEvent(new CustomEvent('response', { bubbles: true, composed: true }));
    }
    entityStore.invalidateHref(this.href);
  }
}

customElements.define('scheduler-locations', SchedulerLocations);
